import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { getGamesByProfessor, createGame } from '../../firebase/db';
import { useToast } from '../Toast';

const ProfessorDashboard = () => {
    const navigate = useNavigate();
    const { currentUser } = useContext(AuthContext);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newGameName, setNewGameName] = useState('');
    const [createdGame, setCreatedGame] = useState(null);
    const [creating, setCreating] = useState(false);
    const { showToast, ToastContainer } = useToast();

    useEffect(() => {
        if (!createdGame) return;
        const timer = setTimeout(() => setCreatedGame(null), 8000);
        return () => clearTimeout(timer);
    }, [createdGame]);

    useEffect(() => {
        const fetchGames = async () => {
            if (!currentUser) return;
            try {
                const professorGames = await getGamesByProfessor(currentUser.uid);
                setGames(professorGames);
            } catch (err) {
                console.error('Error fetching games:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchGames();
    }, [currentUser]);

    const handleCreateGame = async () => {
        if (!newGameName.trim()) return;
        setCreating(true);
        try {
            const result = await createGame(newGameName.trim(), currentUser.uid);
            setCreatedGame(result);
            const updatedGames = await getGamesByProfessor(currentUser.uid);
            setGames(updatedGames);
            setNewGameName('');
            setShowCreateForm(false);
        } catch (err) {
            console.error('Error creating game:', err);
            showToast('Error creating game. Please try again.', 'error');
        } finally {
            setCreating(false);
        }
    };

    const getStudentLink = (gameCode) => {
        return `${window.location.origin}/?game=${gameCode}`;
    };

    const copyLink = (gameCode) => {
        navigator.clipboard.writeText(getStudentLink(gameCode));
        showToast('Student link copied to clipboard!', 'success');
    };

    const activeGames = games.filter(g => g.status === 'active');
    const completedGames = games.filter(g => g.status === 'completed');

    if (loading) {
        return (
            <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', textAlign: 'center', color: '#666' }}>
                Loading dashboard...
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
            {ToastContainer}
            <h1 style={{ textAlign: 'center' }}>Instructor Dashboard</h1>

            {/* Create New Game */}
            {!showCreateForm ? (
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        style={{
                            padding: '12px 24px', backgroundColor: '#4b2e83', color: '#fff',
                            border: 'none', borderRadius: '6px', fontSize: '16px',
                            fontWeight: 'bold', cursor: 'pointer',
                        }}
                    >
                        Create New Game
                    </button>
                </div>
            ) : (
                <div style={{
                    backgroundColor: '#f0f0f0', padding: '20px', borderRadius: '8px',
                    marginBottom: '30px',
                }}>
                    <h3 style={{ margin: '0 0 12px 0' }}>Create New Game</h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="Game name (e.g., Cuban Bikes - Spring 2026)"
                            value={newGameName}
                            onChange={(e) => setNewGameName(e.target.value)}
                            style={{
                                flex: 1, padding: '10px', borderRadius: '6px',
                                border: '1px solid #ddd', fontSize: '14px',
                            }}
                        />
                        <button
                            onClick={handleCreateGame}
                            disabled={creating || !newGameName.trim()}
                            style={{
                                padding: '10px 20px', backgroundColor: '#4b2e83', color: '#fff',
                                border: 'none', borderRadius: '6px', cursor: creating ? 'not-allowed' : 'pointer',
                                opacity: creating || !newGameName.trim() ? 0.6 : 1,
                                fontWeight: 'bold',
                            }}
                        >
                            {creating ? 'Creating...' : 'Create'}
                        </button>
                        <button
                            onClick={() => { setShowCreateForm(false); setNewGameName(''); }}
                            style={{
                                padding: '10px 16px', backgroundColor: '#ccc', color: '#333',
                                border: 'none', borderRadius: '6px', cursor: 'pointer',
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Just-created game link */}
            {createdGame && (
                <div style={{
                    backgroundColor: '#e8f5e9', padding: '16px', borderRadius: '8px',
                    marginBottom: '20px', border: '1px solid #a5d6a7',
                }}>
                    <strong>Game created!</strong> Share this link with your students:
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                        <code style={{
                            flex: 1, padding: '8px', backgroundColor: '#fff',
                            borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px',
                            wordBreak: 'break-all',
                        }}>
                            {getStudentLink(createdGame.gameCode)}
                        </code>
                        <button
                            onClick={() => copyLink(createdGame.gameCode)}
                            style={{
                                padding: '8px 16px', backgroundColor: '#4b2e83', color: '#fff',
                                border: 'none', borderRadius: '4px', cursor: 'pointer',
                                fontWeight: 'bold', whiteSpace: 'nowrap',
                            }}
                        >
                            Copy Link
                        </button>
                    </div>
                </div>
            )}

            {/* Active Games */}
            <h2>Active Games</h2>
            {activeGames.length === 0 ? (
                <p style={{ color: '#999', fontStyle: 'italic' }}>No active games. Create one above.</p>
            ) : (
                activeGames.map(game => (
                    <div
                        key={game.gameId}
                        style={{
                            padding: '16px', margin: '8px 0', backgroundColor: '#e0f7fa',
                            borderRadius: '8px', cursor: 'pointer', color: 'black',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}
                        onClick={() => navigate(`/simulation/${game.gameId}`)}
                    >
                        <div>
                            <strong>{game.name}</strong>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                Code: {game.gameCode}
                            </div>
                        </div>
                        <button
                            onClick={(e) => { e.stopPropagation(); copyLink(game.gameCode); }}
                            style={{
                                padding: '6px 12px', backgroundColor: '#4b2e83', color: '#fff',
                                border: 'none', borderRadius: '4px', cursor: 'pointer',
                                fontSize: '12px',
                            }}
                        >
                            Copy Link
                        </button>
                    </div>
                ))
            )}

            {/* Completed Games */}
            {completedGames.length > 0 && (
                <>
                    <h2 style={{ marginTop: '30px' }}>Completed Games</h2>
                    {completedGames.map(game => (
                        <div
                            key={game.gameId}
                            style={{
                                padding: '16px', margin: '8px 0', backgroundColor: '#ffe0b2',
                                borderRadius: '8px', color: 'black', cursor: 'pointer',
                            }}
                            onClick={() => navigate(`/simulation/${game.gameId}`)}
                        >
                            <strong>{game.name}</strong>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                Code: {game.gameCode}
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default ProfessorDashboard;
